import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

export interface TintableIconProps extends HTMLAttributes<HTMLSpanElement> {
  /** URL of a monochrome icon with a transparent background (svg/png/webp). */
  src: string;
}

/**
 * Remote icon that follows the surrounding text color.
 *
 * Renders the image as a CSS mask over a `currentColor` box instead of an
 * `<img>`: only the icon's alpha channel is used, so the fill color baked
 * into the file is irrelevant and the icon tints with the parent's `color`
 * (hover/active states included). This is the only way to recolor an icon
 * loaded by URL — CSS cannot reach inside an `<img>`.
 *
 * The source must be a monochrome glyph with transparency; full-color
 * artwork would flatten to a silhouette. Size it via `className`.
 *
 * @example
 * ```tsx
 * <span className="text-purple">
 *   <TintableIcon src={cat.iconImgUrl} className="size-[18px]" />
 * </span>
 * ```
 */
const TintableIcon = forwardRef<HTMLSpanElement, TintableIconProps>(({ src, className, style, ...props }, ref) => (
  <span
    ref={ref}
    aria-hidden
    className={cn('inline-block flex-shrink-0 bg-current', className)}
    style={{
      maskImage: `url("${src}")`,
      maskSize: 'contain',
      maskRepeat: 'no-repeat',
      maskPosition: 'center',
      ...style,
    }}
    {...props}
  />
));
TintableIcon.displayName = 'TintableIcon';

export { TintableIcon };
